/**
 * Edicao dos dados basicos da empresa (nome, segmento, status, contato).
 *
 * E um Modal, nao o assistente reaberto: os sete passos existem para criar;
 * para corrigir um nome ou pausar a conta, sete telas seriam castigo. Na
 * previa, nome, segmento e status vao pelo `updateCompany` do contexto (valem
 * em todas as telas) e o contato fica local; quando a rota existir, "Salvar"
 * vira o `PATCH /api/admin/companies/:id` e o formulario nao muda.
 *
 * O formulario e recarregado da base toda vez que o modal ABRE (efeito em
 * `open`): fechar sem salvar descarta o que foi digitado, como a pessoa espera.
 */

import { useEffect, useState } from 'react';
import { Button, Field, Input, Modal, Select } from '../../../components/ui';
import type { CompanyStatus } from '../../../types';
import { STATUS_EMPRESA, STATUS_LISTA, emailValido } from '../vocabulario';

export interface EdicaoBasica {
  readonly nome: string;
  readonly segmento: string;
  readonly status: CompanyStatus;
  readonly contatoNome: string;
  readonly contatoEmail: string;
}

const FORM_ID = 'form-editar-empresa';

function ehStatus(valor: string): valor is CompanyStatus {
  return STATUS_LISTA.some((s) => s === valor);
}

export function ModalEditarEmpresa({
  open,
  base,
  onClose,
  onSave,
}: {
  readonly open: boolean;
  readonly base: EdicaoBasica;
  readonly onClose: () => void;
  readonly onSave: (edicao: EdicaoBasica) => void;
}): JSX.Element | null {
  const [form, setForm] = useState<EdicaoBasica>(base);
  const [tentou, setTentou] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(base);
      setTentou(false);
    }
  }, [open, base]);

  const erros = {
    nome: form.nome.trim() === '' ? 'Informe o nome da empresa.' : null,
    segmento: form.segmento.trim() === '' ? 'Informe o segmento.' : null,
    contatoNome: form.contatoNome.trim() === '' ? 'Informe quem é o contato.' : null,
    contatoEmail: emailValido(form.contatoEmail) ? null : 'Informe um e-mail válido.',
  };
  const valido = Object.values(erros).every((e) => e === null);

  const salvar = (): void => {
    setTentou(true);
    if (!valido) {
      return;
    }
    onSave({
      nome: form.nome.trim(),
      segmento: form.segmento.trim(),
      status: form.status,
      contatoNome: form.contatoNome.trim(),
      contatoEmail: form.contatoEmail.trim().toLowerCase(),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Editar dados básicos"
      description="Nome, segmento e status valem na hora em todas as telas da prévia (lista, cabeçalho, seletor). O contato fica só nesta tela até o cadastro real existir."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" type="submit" form={FORM_ID}>
            Salvar na prévia
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          salvar();
        }}
      >
        <Field label="Nome da empresa" required error={tentou ? erros.nome : null}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} autoComplete="organization" />
          )}
        </Field>
        <Field label="Segmento" required error={tentou ? erros.segmento : null}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={form.segmento} onChange={(e) => setForm((f) => ({ ...f, segmento: e.target.value }))} autoComplete="off" />
          )}
        </Field>
        <Field label="Status" hint="Pausar interrompe a régua: nenhum lead novo entra na fila.">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={form.status}
              onChange={(e) => {
                const valor = e.target.value;
                if (ehStatus(valor)) {
                  setForm((f) => ({ ...f, status: valor }));
                }
              }}
            >
              {STATUS_LISTA.map((s) => (
                <option key={s} value={s}>
                  {STATUS_EMPRESA[s].label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Contato principal" required error={tentou ? erros.contatoNome : null}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={form.contatoNome} onChange={(e) => setForm((f) => ({ ...f, contatoNome: e.target.value }))} autoComplete="name" />
            )}
          </Field>
          <Field label="E-mail do contato" required error={tentou ? erros.contatoEmail : null}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} type="email" inputMode="email" aria-describedby={describedBy} aria-invalid={invalid} value={form.contatoEmail} onChange={(e) => setForm((f) => ({ ...f, contatoEmail: e.target.value }))} autoComplete="email" />
            )}
          </Field>
        </div>
      </form>
    </Modal>
  );
}
